import {
	IButton,
	IComponentsById,
	IComponentType,
	IComponentUnion,
	IContentDoc,
	IEmitter,
	IImage,
	IModel3d,
	ISceneComp,
	IScreenAnchorGroup,
	IScreenAnchorPositionType,
	IScreenContent,
	ISpatialComponentUnion,
	IText,
	IVideo,
} from '../index';

interface IIsValidReport {
	isValid: boolean;
	report: string[];
}

/**
 * Returns true if the component is valid within the supplied componentsById
 * TODO - Only currently works for Scene and ScreenContent component types
 */
export const isValidComponent = (component: IComponentUnion, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	const isValid = true;
	const report = [] as string[];
	switch (component.type) {
		case IComponentType.Scene:
			return isValidScene(component, componentsByIdCp);
		case IComponentType.ScreenContent:
			return isValidScreenContent(component, componentsByIdCp);
		case IComponentType.Model3d:
			return isValidModel3d(component, componentsByIdCp);
		case IComponentType.Image:
			return isValidImage(component, componentsByIdCp);
		case IComponentType.Video:
			return isValidVideo(component, componentsById);
		case IComponentType.Button:
			return isValidButton(component, componentsById);
		case IComponentType.Text:
			return isValidText(component, componentsById);
		case IComponentType.Emitter:
			return isValidEmitter(component, componentsById);
		default:
			// Remove once all component types covered
			return {
				isValid,
				report,
			};
	}
};

const isIdPropertyValidOnSpatialComponent = (component: ISpatialComponentUnion, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	// ID property should be
	if (!component.id) {
		isValid = false;
		report.push(`Missing ID key in component: ${JSON.stringify(component)}`);
	} else {
		// ID property should match the key in the componentsById
		const componentsWithThisId = Object.keys(componentsByIdCp).filter((componentId) => componentId == component.id);
		if (componentsWithThisId.length == 0) {
			isValid = false;
			report.push(`ID for component: ${component.id} (type: ${component.type}) not found in componentsById`);
		}
		if (componentsWithThisId.length > 1) {
			isValid = false;
			report.push(`ID for component: ${component.id} (type: ${component.type}) found more than once in componentsById`);
		}
	}
	return {
		isValid,
		report,
	};
};

export const isValidModel3d = (component: IModel3d, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

export const isValidImage = (component: IImage, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

export const isValidVideo = (component: IVideo, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

export const isValidEmitter = (component: IEmitter, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

export const isValidText = (component: IText, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

export const isValidButton = (component: IButton, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	const { isValid: innerIsValid, report: innerReport } = isIdPropertyValidOnSpatialComponent(component, componentsByIdCp);
	if (!innerIsValid) {
		isValid = false;
		report.push(...innerReport);
	}

	return {
		isValid,
		report,
	};
};

/**
 * True if component param is a valid scene component. Specifically:
 * - There is only 1x ScreenContent per scene
 * TODO - only face tracked scenes can contain face landmark groups
 */
export const isValidScene = (component: ISceneComp, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	// There should always be 1x ScreenContent per scene
	const screenContentCount = component.children.filter((componentId: string) => {
		if (!componentsByIdCp[componentId]?.type) return true; // If no type, assume its a ScreenContent ( safety first ) to fail this test
		return componentsByIdCp[componentId]?.type === IComponentType.ScreenContent;
	}).length;
	if (screenContentCount !== 1) {
		isValid = false;
		report.push(`More than 1 ScreenContent component in scene ${JSON.stringify(component)}`);
	}

	return {
		isValid,
		report,
	};
};

/**
 * True if component param is a valid screen content component.
 * Specifically:
 * - It only contains screen anchor groups
 * - There is 0 or 1 of each anchor position group in it's children
 */
export const isValidScreenContent = (component: IScreenContent, componentsById: IComponentsById): IIsValidReport => {
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	let isValid = true;
	const report = [] as string[];

	// A screen content layer can ONLY contain screen anchor groups
	for (const anchorGroupId of component.children) {
		const anchorGroup = componentsByIdCp[anchorGroupId];
		if (anchorGroup.type !== IComponentType.ScreenAnchorGroup) {
			isValid = false;
			report.push(`Screen content component: ${JSON.stringify(component)} contains non-anchor group component ${JSON.stringify(anchorGroup)}`);
		}
	}

	// A screen content layer should have 0 or 1 of each IScreenAnchorPositionType
	for (const anchorPositionType in IScreenAnchorPositionType) {
		const count = component.children.reduce((acc, anchorGroupId) => {
			const anchorGroup = componentsByIdCp[anchorGroupId] as IScreenAnchorGroup;
			if (anchorGroup.anchorPositionType === anchorPositionType) return acc + 1;
			return acc;
		}, 0);
		if (count > 1) {
			isValid = false;
			report.push(`Screen content component: ${JSON.stringify(component)} contains more than 1 screen anchor group with the same anchorPositionType`);
		}
	}

	return {
		isValid,
		report,
	};
};

/**
 * Checks componentsById and verifies it is valid. Returns IIsValidReport
 */
export const isValidComponentsById = (componentsById: IComponentsById): IIsValidReport => {
	let isValid = true;
	const report = [] as string[];
	const componentsByIdCp = JSON.parse(JSON.stringify(componentsById)) as IComponentsById;
	const componentsByIdArr = Object.entries(componentsByIdCp);
	for (let i = 0; i < componentsByIdArr.length; i++) {
		const component = componentsByIdArr[i][1];
		const { isValid: componentIsValid, report: componentReport } = isValidComponent(component, componentsByIdCp);
		if (!componentIsValid) {
			isValid = false;
			report.push(...componentReport);
		}
	}

	return {
		isValid,
		report,
	};
};

export const isValidContentDoc = (contentDoc: IContentDoc): IIsValidReport => {
	let isValid = true;
	const report = [] as string[];

	if (typeof contentDoc === 'undefined') {
		isValid = false;
		report.push(`ContentDoc is undefined`);
	} else {
		const contentDocCp = JSON.parse(JSON.stringify(contentDoc)) as IContentDoc;
		if (!!contentDocCp && !!contentDocCp?.componentsById) {
			return isValidComponentsById(contentDocCp.componentsById);
		} else {
			isValid = false;
			report.push(`ComponentsById is undefined`);
		}
	}
	return {
		isValid,
		report,
	};
};
